"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAppContext } from "@/context/AppContext";

export function CookieConsent() {
  const { theme } = useAppContext();
  const [showConsent, setShowConsent] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const [cookies, setCookies] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem("CheckIn-Out-Pro_cookie_consent");
    if (!saved) setShowConsent(true);
    else setCookies(JSON.parse(saved));
  }, []);

  const handleAllowAll = () => {
    const all = { essential: true, analytics: true, marketing: true };
    localStorage.setItem("CheckIn-Out-Pro_cookie_consent", JSON.stringify(all));
    setCookies(all);
    setShowConsent(false);
  };

  const handleSave = () => {
    localStorage.setItem(
      "CheckIn-Out-Pro_cookie_consent",
      JSON.stringify(cookies),
    );
    setShowManage(false);
    setShowConsent(false);
  };

  return (
    <>
      {/* Banner */}
      {showConsent && !showManage && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[90%] md:w-auto
          rounded-2xl border backdrop-blur-xl px-5 py-4 md:px-6 md:py-5 shadow-lg
          flex flex-col md:flex-row items-center gap-4 transition-all duration-300
          ${
            theme === "dark"
              ? "bg-gray-900/80 border-white/10 text-gray-100"
              : "bg-gray-50/95 border-gray-300 text-gray-900"
          }`}
        >
          <p className="text-sm text-center md:text-left leading-relaxed">
            We use cookies to enhance your experience and analyze usage. You can
            manage your preferences below or accept all.
          </p>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowManage(true)}
              className={`border ${
                theme === "dark"
                  ? "border-white/30 text-gray-100 hover:bg-white/10"
                  : "border-gray-400 text-gray-800 hover:bg-gray-100"
              }`}
            >
              Manage Cookies
            </Button>
            <Button
              onClick={handleAllowAll}
              className={`${
                theme === "dark"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-emerald-500 hover:bg-emerald-600 text-white"
              }`}
            >
              Allow All
            </Button>
          </div>
        </div>
      )}

      {/* Manage Cookies Modal */}
      <Dialog open={showManage} onOpenChange={setShowManage}>
        <DialogContent
          className={`backdrop-blur-xl border max-w-lg rounded-2xl transition-all duration-300
          ${
            theme === "dark"
              ? "bg-gray-900/80 border-white/10 text-gray-100"
              : "bg-white/95 border-gray-300 text-gray-900"
          }`}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold mb-2">
              Manage Cookie Preferences
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-sm">
            {/* Essential */}
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Essential Cookies</p>
                <p
                  className={`text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Required for core site functionality and security.
                </p>
              </div>
              <Switch checked disabled />
            </div>

            {/* Analytics */}
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Analytics Cookies</p>
                <p
                  className={`text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Help us understand how users interact with the app.
                </p>
              </div>
              <Switch
                checked={cookies.analytics}
                onCheckedChange={(v) =>
                  setCookies({ ...cookies, analytics: v })
                }
              />
            </div>

            {/* Marketing */}
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Marketing Cookies</p>
                <p
                  className={`text-xs ${
                    theme === "dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Used for relevant offers and personalized promotions.
                </p>
              </div>
              <Switch
                checked={cookies.marketing}
                onCheckedChange={(v) =>
                  setCookies({ ...cookies, marketing: v })
                }
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowManage(false)}
                className={`border ${
                  theme === "dark"
                    ? "border-white/30 text-gray-100 hover:bg-white/10"
                    : "border-gray-400 text-gray-800 hover:bg-gray-100"
                }`}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className={`${
                  theme === "dark"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-emerald-500 hover:bg-emerald-600 text-white"
                }`}
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
